import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getConfigValue } from '@/lib/config'
import { buildShopifyMembershipUrl, buildShopifyUrl, resolveReferralCode } from '@/lib/referral'

type NavGroupKey = 'shop' | 'science' | 'explore'

type NavLinkItem = {
  label: string
  detail: string
  href: string
  tag?: string
}

function landingHref(hash: string) {
  return `/${hash.startsWith('#') ? hash : `#${hash}`}`
}

export default function TopBar() {
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeDesktopMenu, setActiveDesktopMenu] = useState<NavGroupKey | null>(null)
  const [activeMobileMenu, setActiveMobileMenu] = useState<NavGroupKey | null>(null)
  const isWebsiteTest = location.pathname === '/' || location.pathname === '/website-test'
  const isCurrentPublicSite =
    isWebsiteTest ||
    location.pathname === '/about' ||
    location.pathname.startsWith('/research/')
  const isLandingV2 = location.pathname === '/landing-v2'
  const isOfferLanding = isLandingV2 || isCurrentPublicSite
  const isInviteExperience = location.pathname === '/invite-v2-preview' || location.pathname === '/invite' || location.pathname.startsWith('/invite/')
  const isMembersSection = location.pathname.startsWith('/dashboard')
  const dashboardUrl = getConfigValue('DASHBOARD_URL')
  const referralCode = resolveReferralCode()
  const productUrl = buildShopifyMembershipUrl(referralCode)
  const threeMonthUrl = buildShopifyUrl('/products/aelo-straw-filter-3-month-supply', referralCode)
  const merchUrl = buildShopifyUrl('/collections/all', referralCode)
  const contactUrl = buildShopifyUrl('/pages/contact', referralCode)
  const joinUrl = isCurrentPublicSite
    ? productUrl
    : isInviteExperience || isLandingV2
      ? productUrl
      : dashboardUrl
  const joinLabel =
    isCurrentPublicSite || isInviteExperience || isLandingV2 ? 'Try Aelo free' : 'Member Dashboard'
  const showDashboardLink = (isInviteExperience || isOfferLanding) && !isMembersSection
  const pageHref = (hash: string) => (isWebsiteTest ? hash : landingHref(hash))
  const navGroups: Record<NavGroupKey, NavLinkItem[]> = {
    shop: isCurrentPublicSite
      ? [
          {
            label: 'Start your free trial',
            detail:
              'Get the Essential Straw Kit for free ($80 value) when you start your free trial, just pay shipping.',
            href: pageHref('#free-trial'),
          },
        ]
      : [
          {
            label: 'One-Month Filter Supply',
            detail: '$35 monthly after the 14-day free trial.',
            href: productUrl,
            tag: 'Best start',
          },
          {
            label: 'Three-Month Filter Supply',
            detail: '$80 every three months for one 3-month supply pack.',
            href: threeMonthUrl,
            tag: 'Most popular',
          },
          {
            label: 'Merch + All Products',
            detail: 'Browse the storefront, apparel, and launch products.',
            href: merchUrl,
          },
        ],
    science: [
      {
        label: 'Microplastics 101',
        detail: 'What microplastics are and what they do to human health.',
        href: pageHref(isCurrentPublicSite ? '#health' : '#faq'),
      },
      {
        label: 'Research Library',
        detail: 'Peer-reviewed research on microplastics and their effects on human health.',
        href: pageHref(isCurrentPublicSite ? '#research' : '#science'),
      },
      {
        label: 'How Aelo Helps',
        detail: 'A portable final filtration step for bottles, tumblers, and refills.',
        href: pageHref('#solution'),
      },
    ],
    explore: isCurrentPublicSite
      ? [
          {
            label: 'Free trial offer',
            detail:
              'Get the Essential Straw Kit for free ($80 value) when you start your free trial, just pay shipping.',
            href: pageHref('#free-trial'),
            tag: 'Limited',
          },
          {
            label: 'Referral rewards',
            detail: 'Invite friends, get free stuff.',
            href: pageHref('#rewards'),
          },
          {
            label: 'About Aelo',
            detail: "Our founder's water legacy and why Aelo exists.",
            href: pageHref('#about'),
          },
          {
            label: 'FAQ',
            detail: 'You have questions, we have answers.',
            href: pageHref('#faq'),
          },
        ]
      : [
          {
            label: 'Prelaunch Program',
            detail: 'Try the Essential Straw Kit free for 14 days; pay $5.99 shipping today.',
            href: pageHref('#ladder'),
            tag: 'Limited',
          },
          {
            label: 'Referral Rewards',
            detail: 'Invite friends after checkout and earn future filter credits.',
            href: pageHref('#ladder'),
          },
          {
            label: 'About Aelo',
            detail: 'Robert Brown’s water legacy and why Aelo exists.',
            href: '/about',
          },
          {
            label: 'Questions + Contact',
            detail: 'Read FAQs or reach the Aelo team.',
            href: contactUrl,
          },
        ],
  }
  const navLabels: Record<NavGroupKey, string> = {
    shop: 'Shop',
    science: 'Science',
    explore: 'Explore',
  }

  useEffect(() => {
    setMenuOpen(false)
    setActiveDesktopMenu(null)
    setActiveMobileMenu(null)
  }, [location.pathname, location.hash])

  function handleMobileMenuToggle() {
    setMenuOpen((open) => {
      const nextOpen = !open
      if (nextOpen) {
        setActiveMobileMenu(null)
      }
      return nextOpen
    })
  }

  return (
    <header
      className={cn(
        'top-0 z-50 w-full border-b border-aelo-mist bg-white/80 backdrop-blur-md',
        isWebsiteTest ? 'fixed inset-x-0' : 'sticky',
      )}
    >
      <div className="aelo-container flex h-16 items-center justify-between gap-4 md:h-[4.5rem]">
        <Link to="/" className="flex items-center gap-2 text-aelo-black">
          <img src="/assets/aelo-logo.png?v=20260509b" alt="Aelo" className="block h-8 w-auto -translate-y-px md:h-9" />
        </Link>

        <nav className="hidden flex-1 items-center gap-2 md:flex" aria-label="Primary">
          {(Object.keys(navGroups) as NavGroupKey[]).map((group) => (
            <div
              key={group}
              className="relative"
              onMouseEnter={() => setActiveDesktopMenu(group)}
              onMouseLeave={() => setActiveDesktopMenu(null)}
              onFocus={() => setActiveDesktopMenu(group)}
            >
              <button
                type="button"
                aria-expanded={activeDesktopMenu === group}
                onClick={() => setActiveDesktopMenu(activeDesktopMenu === group ? null : group)}
                className={cn(
                  'inline-flex h-11 items-center gap-1 rounded-full px-4 text-base font-medium text-aelo-black transition-colors hover:bg-aelo-snow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-aelo-water',
                  activeDesktopMenu === group && 'bg-aelo-snow',
                )}
              >
                {navLabels[group]}
                <ChevronDown className={cn('h-4 w-4 transition-transform', activeDesktopMenu === group && 'rotate-180')} />
              </button>
              {activeDesktopMenu === group && (
                <div
                  className="absolute left-0 top-full w-[31rem] pt-3"
                  onMouseEnter={() => setActiveDesktopMenu(group)}
                  onMouseLeave={() => setActiveDesktopMenu(null)}
                >
                  <div className="overflow-hidden rounded-[1.35rem] border border-aelo-mist bg-white p-3 shadow-[0_24px_80px_rgba(10,10,10,0.14)]">
                    <div className="grid gap-1">
                      {navGroups[group].map((item) => (
                        <a
                          key={`${group}-${item.label}`}
                          href={item.href}
                          className="group grid gap-1 rounded-[1rem] p-4 text-left transition-colors hover:bg-aelo-snow focus-visible:bg-aelo-snow focus-visible:outline-none"
                        >
                          <span className="flex items-center justify-between gap-4">
                            <span className="text-base font-semibold text-aelo-black">{item.label}</span>
                            {item.tag ? (
                              <span className="rounded-full bg-aelo-water-muted px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.12em] text-aelo-water-deep">
                                {item.tag}
                              </span>
                            ) : (
                              <ArrowRight className="h-4 w-4 text-aelo-silver transition-transform group-hover:translate-x-1" />
                            )}
                          </span>
                          <span className="max-w-sm text-sm leading-5 text-aelo-slate/72">{item.detail}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <nav className="flex items-center gap-2 sm:gap-3" aria-label="Actions">
          {showDashboardLink && (
            <a
              href={dashboardUrl}
              className="hidden h-10 items-center rounded-full border border-aelo-mist bg-white px-5 text-sm font-semibold text-aelo-black transition-colors hover:bg-aelo-snow md:inline-flex"
            >
              Member Dashboard
            </a>
          )}
          <a
            href={joinUrl}
            className={cn(
              'h-10 items-center rounded-full border border-aelo-black bg-aelo-black text-sm font-semibold text-white transition-colors hover:bg-aelo-charcoal',
              isWebsiteTest ? 'inline-flex px-4 sm:px-5' : 'hidden px-5 md:inline-flex',
              location.pathname.startsWith('/dashboard') && 'border-aelo-mist bg-aelo-snow text-aelo-black',
            )}
          >
            {joinLabel}
          </a>
          <button
            type="button"
            aria-controls="mobile-topbar-menu"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={handleMobileMenuToggle}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-aelo-mist bg-white text-aelo-black transition-colors hover:bg-aelo-snow md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </div>
      {menuOpen && (
        <div id="mobile-topbar-menu" className="border-t border-aelo-mist bg-white md:hidden">
          <div className="aelo-container grid gap-3 py-4">
            {!isLandingV2 && (
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="flex min-h-12 w-full items-center rounded-2xl border border-aelo-mist bg-white px-4 text-left text-base font-semibold text-aelo-black transition-colors hover:bg-aelo-snow"
              >
                Home
              </Link>
            )}
            {(Object.keys(navGroups) as NavGroupKey[]).map((group) => (
              <div key={`mobile-${group}`} className="rounded-2xl border border-aelo-mist bg-white">
                <button
                  type="button"
                  aria-expanded={activeMobileMenu === group}
                  onClick={() => setActiveMobileMenu(activeMobileMenu === group ? null : group)}
                  className="flex min-h-12 w-full items-center justify-between px-4 text-left text-base font-semibold text-aelo-black"
                >
                  {navLabels[group]}
                  <ChevronDown className={cn('h-4 w-4 text-aelo-silver transition-transform', activeMobileMenu === group && 'rotate-180')} />
                </button>
                {activeMobileMenu === group && (
                  <div className="grid gap-1 border-t border-aelo-mist p-2">
                    {navGroups[group].map((item) => (
                      <a
                        key={`mobile-${group}-${item.label}`}
                        href={item.href}
                        onClick={() => setMenuOpen(false)}
                        className="grid min-h-14 gap-1 rounded-xl px-3 py-3 text-left transition-colors hover:bg-aelo-snow"
                      >
                        <span className="flex items-center justify-between gap-3 text-sm font-semibold text-aelo-black">
                          {item.label}
                          {item.tag && <span className="rounded-full bg-aelo-water-muted px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.1em] text-aelo-water-deep">{item.tag}</span>}
                        </span>
                        <span className="text-xs leading-5 text-aelo-slate/72">{item.detail}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {!isMembersSection && (
              <div className="grid gap-2">
                <a
                  href={dashboardUrl}
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-aelo-mist bg-white px-5 text-sm font-semibold text-aelo-black transition-colors hover:bg-aelo-snow"
                >
                  Member Dashboard
                </a>
                <a
                  href={joinUrl}
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex min-h-12 items-center justify-center rounded-full bg-aelo-black px-5 text-sm font-semibold text-white transition-colors hover:bg-aelo-charcoal"
                >
                  {joinLabel}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
